import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, ConfirmDialog, Skeleton, useToast } from '@bbpos/ui';
import { domainService } from '@/services/site.service';
import { GLASS_PANEL, cn } from './tenant-ui';
import { DOMAIN_STATUS_LABELS } from '@/types/site';
import type { DomainVerifyResult, TenantDomain, TenantDomainStatus } from '@/types/site';

/**
 * A tenant's custom domain.
 *
 * A CONFIG PANEL, not a DataTable: it is one domain per tenant, so the shape
 * follows TenantWebsiteTab rather than TenantLocationsTab. The destructive
 * actions (change, park, remove) all go through ConfirmDialog because each one
 * leaves infrastructure behind that only an operator can clean up.
 */

const STATUS_TONE: Record<TenantDomainStatus, 'success' | 'warning' | 'danger' | 'neutral'> = {
  ACTIVE: 'success',
  VERIFIED: 'warning',
  PENDING: 'warning',
  FAILED: 'danger',
  DISABLED: 'neutral',
};

function LinkIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
      <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
    </svg>
  );
}

function CopyField({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <div>
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400">{label}</p>
      <div className="mt-1 flex items-center gap-2">
        <code className="flex-1 truncate rounded-lg bg-neutral-100 px-3 py-2 font-mono text-xs text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200">
          {value}
        </code>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            toast.toast({ variant: 'success', title: 'Copied' });
          }}
        >
          Copy
        </Button>
      </div>
    </div>
  );
}

export function TenantDomainPanel({
  tenantId,
  isAdminPlus,
  isSuperAdmin,
  hasWebsite,
}: {
  tenantId: string;
  isAdminPlus: boolean;
  isSuperAdmin: boolean;
  /** A domain FKs the website row, so the API rejects one without it. */
  hasWebsite: boolean;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const [hostname, setHostname] = useState('');
  const [changeTo, setChangeTo] = useState('');
  const [confirm, setConfirm] = useState<'change' | 'park' | 'remove' | null>(null);
  const [lastVerify, setLastVerify] = useState<DomainVerifyResult | null>(null);
  const [teardown, setTeardown] = useState<string | null>(null);

  const query = useQuery({
    queryKey: ['tenant-domain', tenantId],
    queryFn: () => domainService.get(tenantId),
    // No domain is the normal starting state, not an error — same precedent as
    // TenantWebsiteTab's notAllowlisted.
    retry: false,
    enabled: hasWebsite,
  });
  const domain = query.data;
  const noDomain = query.isError && !domain;

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant-domain', tenantId] });
  };

  const fail = (err: unknown) => toast.toast({ variant: 'error', title: (err as Error).message || 'Something went wrong' });

  const create = useMutation({
    mutationFn: () => domainService.create(tenantId, { hostname: hostname.trim() }),
    onSuccess: () => {
      setHostname('');
      invalidate();
      toast.toast({ variant: 'success', title: 'Domain claimed', description: 'Send the customer the DNS records below.' });
    },
    onError: fail,
  });

  const verify = useMutation({
    mutationFn: () => domainService.verify(tenantId),
    onSuccess: (result) => {
      setLastVerify(result);
      invalidate();
      if (result.ok) toast.toast({ variant: 'success', title: `Verified via ${result.verified_via}` });
      else toast.toast({ variant: 'error', title: result.detail ?? 'Not verified yet' });
    },
    onError: fail,
  });

  const rotate = useMutation({
    mutationFn: () => domainService.rotateToken(tenantId),
    onSuccess: () => {
      invalidate();
      toast.toast({ variant: 'success', title: 'New token issued', description: 'The customer must republish the TXT record.' });
    },
    onError: fail,
  });

  const change = useMutation({
    mutationFn: () => domainService.update(tenantId, { hostname: changeTo.trim() }),
    onSuccess: (result) => {
      setChangeTo('');
      setConfirm(null);
      invalidate();
      setTeardown(result.teardown_required ? (result.teardown_command ?? null) : null);
      toast.toast({ variant: 'success', title: 'Domain changed', description: 'Back to PENDING until DNS is verified.' });
    },
    onError: (err) => {
      setConfirm(null);
      fail(err);
    },
  });

  const park = useMutation({
    mutationFn: () => domainService.update(tenantId, { status: 'DISABLED' }),
    onSuccess: (result) => {
      setConfirm(null);
      invalidate();
      setTeardown(result.teardown_command ?? null);
      toast.toast({ variant: 'success', title: 'Domain parked', description: 'The website is back on its Orio360 address.' });
    },
    onError: (err) => {
      setConfirm(null);
      fail(err);
    },
  });

  const remove = useMutation({
    mutationFn: () => domainService.remove(tenantId),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
      toast.toast({ variant: 'success', title: 'Domain removed' });
    },
    onError: (err) => {
      setConfirm(null);
      fail(err);
    },
  });

  if (!hasWebsite) {
    return (
      <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
        <Alert
          variant="info"
          title="No website yet"
          description="Allowlist a website for this tenant first — a custom domain points at one."
        />
      </div>
    );
  }

  if (query.isLoading) {
    return (
      <div className={cn(GLASS_PANEL, 'p-6 space-y-3')}>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {teardown && (
        /*
         * The most important thing on this panel. Changing or parking a domain
         * leaves the old nginx vhost answering and its certificate renewing,
         * for a hostname no row knows about any more — indefinitely, and
         * invisibly, unless someone runs this.
         */
        <Alert
          variant="warning"
          title="Server cleanup still needed"
          dismissible
          onDismiss={() => setTeardown(null)}
          description={
            <>
              <p className="text-sm">
                The old address still has an nginx vhost and a live certificate. Until this runs,
                it keeps answering and keeps renewing:
              </p>
              <code className="mt-2 block rounded-lg bg-warning-100 px-3 py-2 font-mono text-xs dark:bg-warning-900/40">
                {teardown}
              </code>
            </>
          }
        />
      )}

      {noDomain ? (
        <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-brand-500 to-indigo-500 text-white">
              <LinkIcon />
            </div>
            <div>
              <h3 className="font-semibold text-neutral-900 dark:text-white">Custom domain</h3>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                Point the customer&apos;s own domain at their storefront.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              value={hostname}
              onChange={(e) => setHostname(e.target.value)}
              placeholder="sweetshop.com"
              disabled={!isAdminPlus}
              className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm transition-all duration-200 focus:border-brand-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
            />
            <Button
              onClick={() => create.mutate()}
              loading={create.isPending}
              disabled={!isAdminPlus || hostname.trim().length < 4}
            >
              Claim domain
            </Button>
          </div>
          <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
            A scheme, port, path or leading <code>www.</code> are all fine — they get stripped.
          </p>
        </div>
      ) : (
        domain && (
          <>
            <DomainSummary domain={domain} />

            <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in space-y-5')}>
              <div>
                <h4 className="font-semibold text-neutral-900 dark:text-white">
                  DNS the customer must add
                </h4>
                <ul className="mt-2 space-y-1">
                  {domain.dns_instructions.map((line) => (
                    <li
                      key={line}
                      className="rounded-lg bg-neutral-100 px-3 py-2 font-mono text-xs text-neutral-800 dark:bg-neutral-800 dark:text-neutral-200"
                    >
                      {line}
                    </li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                  Or, if their DNS is proxied (Cloudflare orange cloud), the TXT record below
                  instead — it proves control without needing traffic to reach us.
                </p>
              </div>

              <CopyField label="TXT record name" value={domain.txt_record.name} />
              <CopyField label="TXT record value" value={domain.txt_record.value} />

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => verify.mutate()} loading={verify.isPending} disabled={!isAdminPlus}>
                  Verify now
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => rotate.mutate()}
                  loading={rotate.isPending}
                  disabled={!isAdminPlus}
                >
                  Rotate token
                </Button>
              </div>

              {lastVerify && <VerifyResult result={lastVerify} />}
            </div>

            <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in space-y-4')}>
              <h4 className="font-semibold text-neutral-900 dark:text-white">Change domain</h4>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  value={changeTo}
                  onChange={(e) => setChangeTo(e.target.value)}
                  placeholder="sweetshop.in"
                  disabled={!isAdminPlus}
                  className="flex-1 rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm transition-all duration-200 focus:border-brand-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:text-white"
                />
                <Button
                  variant="secondary"
                  onClick={() => setConfirm('change')}
                  disabled={!isAdminPlus || changeTo.trim().length < 4}
                >
                  Change
                </Button>
              </div>

              <div className="flex flex-wrap gap-2 border-t border-neutral-200 pt-4 dark:border-neutral-700">
                {domain.status === 'ACTIVE' && (
                  <Button variant="secondary" onClick={() => setConfirm('park')} disabled={!isAdminPlus}>
                    Park domain
                  </Button>
                )}
                <Button
                  variant="danger"
                  onClick={() => setConfirm('remove')}
                  disabled={!isSuperAdmin || domain.status === 'ACTIVE'}
                >
                  Remove
                </Button>
                {domain.status === 'ACTIVE' && (
                  <p className="self-center text-xs text-neutral-500 dark:text-neutral-400">
                    Park it before removing — otherwise the server keeps serving this hostname.
                  </p>
                )}
              </div>
            </div>
          </>
        )
      )}

      <ConfirmDialog
        open={confirm === 'change'}
        title={`Change to ${changeTo.trim()}?`}
        // Spelled out rather than summarised: this is a one-way door for the
        // customer's SEO and it leaves real infrastructure behind.
        description={`The current address stops working immediately and does NOT redirect on its own. Its nginx vhost and certificate stay on the server until you run the teardown command, which will be shown after this. The new domain starts at PENDING and needs DNS verification plus a new certificate before it goes live.`}
        confirmLabel="Change domain"
        variant="danger"
        loading={change.isPending}
        onConfirm={() => change.mutate()}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm === 'park'}
        title="Park this domain?"
        description="The website reverts to its Orio360 address immediately and the custom domain stops resolving to this tenant. The certificate is left in place so it can be un-parked quickly."
        confirmLabel="Park domain"
        variant="danger"
        loading={park.isPending}
        onConfirm={() => park.mutate()}
        onCancel={() => setConfirm(null)}
      />

      <ConfirmDialog
        open={confirm === 'remove'}
        title="Remove this domain?"
        description="The claim is dropped and the hostname becomes available to any tenant. Remove the nginx vhost and certificate on the server first."
        confirmLabel="Remove"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

function DomainSummary({ domain }: { domain: TenantDomain }) {
  const tone = STATUS_TONE[domain.status];
  return (
    <div className={cn(GLASS_PANEL, 'p-6 animate-fade-in')}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex h-10 w-10 items-center justify-center rounded-lg text-white',
              domain.status === 'ACTIVE'
                ? 'bg-gradient-to-br from-teal-500 to-success-500'
                : 'bg-gradient-to-br from-brand-500 to-indigo-500',
            )}
          >
            <LinkIcon />
          </div>
          <div>
            <p className="font-mono font-semibold text-neutral-900 dark:text-white">
              {domain.hostname}
            </p>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {domain.is_apex ? 'Apex domain' : 'Subdomain'}
              {domain.www_status === 'ACTIVE' && ' · www redirects here'}
              {domain.www_status === 'SKIPPED' && ' · www not on the certificate'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={tone}>{DOMAIN_STATUS_LABELS[domain.status]}</Badge>
          {domain.live_url && (
            <a
              href={domain.live_url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium text-brand-600 transition-all duration-200 hover:text-brand-700 dark:text-brand-400"
            >
              Open ↗
            </a>
          )}
        </div>
      </div>

      {domain.status === 'VERIFIED' && (
        <Alert
          variant="info"
          className="mt-4"
          title="Ready to provision"
          description={
            <>
              DNS is proven. Run{' '}
              <code>scripts/add-tenant-domain.sh {domain.hostname}</code> on the server to issue
              the certificate and go live.
            </>
          }
        />
      )}

      {domain.last_check_status === 'FAILED' && (
        <Alert
          variant="danger"
          className="mt-4"
          title={`Last check failed (${domain.consecutive_failures}×)`}
          description={domain.last_check_error ?? undefined}
        />
      )}

      {(domain.cert_expires_at || domain.last_checked_at) && (
        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Fact label="Verified via" value={domain.verified_via ?? '—'} />
          <Fact
            label="Certificate expires"
            value={domain.cert_expires_at ? new Date(domain.cert_expires_at).toLocaleDateString() : '—'}
          />
          <Fact label="Issuer" value={domain.cert_issuer ?? '—'} />
          <Fact
            label="Last checked"
            value={domain.last_checked_at ? new Date(domain.last_checked_at).toLocaleString() : '—'}
          />
        </dl>
      )}
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-neutral-500 dark:text-neutral-400">{label}</dt>
      <dd className="mt-0.5 font-medium text-neutral-900 dark:text-white">{value}</dd>
    </div>
  );
}

function VerifyResult({ result }: { result: DomainVerifyResult }) {
  if (result.ok) {
    return (
      <Alert
        variant="success"
        title={`Verified via ${result.verified_via}`}
        description={result.next_step ?? undefined}
      />
    );
  }
  return (
    <Alert
      variant={result.reason === 'SERVER_IP_NOT_CONFIGURED' ? 'danger' : 'warning'}
      title={result.detail ?? result.reason ?? 'Not verified'}
      description={
        /*
          What DNS actually answered. Support otherwise has to ask someone with
          shell access to run dig, and the answer is already in the response.
        */
        <dl className="grid gap-1 font-mono text-xs">
          {(['a', 'aaaa', 'cname', 'txt'] as const).map((k) =>
            result.observed[k].length > 0 ? (
              <div key={k}>
                <span className="text-neutral-500">{k.toUpperCase()}: </span>
                {result.observed[k].join(', ')}
              </div>
            ) : null,
          )}
        </dl>
      }
    />
  );
}
